/**
 * Marketplace listing detail + place-an-offer (web).
 *
 * Reads a single listing via GET /marketplace/listings/:id (hand-rolled client,
 * src/api/marketplace.ts — orval not yet regenerated for /marketplace) and, for
 * a buyer, exposes a "Place an offer" form. A listing is a WHOLE-LOT bid: the
 * offer covers the listing's full quantity (qtyKg), so the buyer sets only a
 * price-per-kg. We convert that ZAR/kg decimal to a micro-ZARP (1e-6 ZARP)
 * STRING for the wire and POST it via createOffer(id, {offerPriceZarpPerKgMicro}).
 *
 * KYC gate: the server requires fica_status=verified and otherwise raises a 403
 * with body { error: 'buyer_kyc_required', ... } (services/api/.../buyer-kyc.util.ts).
 * We detect that case and swap the form for a friendly "you must be verified"
 * notice linking to /buyer-verification, rather than dumping a raw error.
 *
 * Back navigation returns to /marketplace. Money is rendered with the @fse/ui
 * <Money>. i18n keys live under marketplace.* in all four web locales.
 */
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Icon,
  IconButton,
  Money,
  Stack,
  StatusPill,
  Text,
  TextField,
  type ChipColor,
  type StatusPillVariant,
} from '@fse/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { FseApiError, readAccessTokenClaims } from '../api/client';
import {
  createOffer,
  getListing,
  microToZarp,
  type ListingDto,
  type ListingStatus,
} from '../api/marketplace';
import { useWatchlist } from '../api/useWatchlist';
import { ListingPhoto } from './ListingPhoto';

const MICRO_PER_ZAR = 1_000_000;

/**
 * ZAR-per-kg user input (a decimal string like "12.50") → micro-ZARP integer
 * STRING for the wire. Rounds to the nearest micro-unit. Returns null when the
 * input isn't a positive finite number, so the form can validate before POSTing.
 * (Mirrors apps/mobile/src/api/marketplace.ts zarPerKgToMicroString — the web
 * marketplace client doesn't export a forward converter, only microToZarp.)
 */
function zarPerKgToMicroString(input: string): string | null {
  const n = Number(input);
  if (!Number.isFinite(n) || n <= 0) return null;
  return String(Math.round(n * MICRO_PER_ZAR));
}

/** Listing lifecycle → StatusPill variant. */
const STATUS_PILL: Record<ListingStatus, StatusPillVariant> = {
  draft: 'upcoming',
  active: 'active',
  paused: 'due',
  sold: 'sold',
  withdrawn: 'upcoming',
};

/**
 * True when a thrown error is the 403 buyer-KYC gate. The server raises a
 * ForbiddenException with body { error: 'buyer_kyc_required', ... }; we read
 * that off FseApiError.body (also tolerating a `code` key), and fall back to
 * treating ANY 403 on offer-create as the gate, since that's the only 403 the
 * endpoint raises for a buyer.
 */
function isBuyerKycRequired(err: unknown): boolean {
  if (!(err instanceof FseApiError)) return false;
  if (err.status !== 403) return false;
  const body = err.body;
  if (body && typeof body === 'object') {
    const tag =
      ('error' in body ? String((body as { error: unknown }).error) : '') ||
      ('code' in body ? String((body as { code: unknown }).code) : '');
    if (tag === 'buyer_kyc_required') return true;
  }
  if (/buyer_kyc_required|kyc/i.test(err.message)) return true;
  // Only 403 the offer-create endpoint raises for a buyer is the KYC gate.
  return true;
}

export function MarketplaceListingScreen() {
  const { t } = useTranslation();
  const watchlist = useWatchlist();

  // Route is wired manually in router.ts with path '/marketplace/$id'. Read the
  // param loosely (strict: false) so this file doesn't depend on a generated
  // route-id type (the tree is assembled by hand).
  const params = useParams({ strict: false }) as { id?: string };
  const listingId = params.id ?? '';

  const listingQuery = useQuery({
    queryKey: ['marketplace-listing', listingId],
    queryFn: ({ signal }) => getListing(listingId, signal),
    enabled: listingId.length > 0,
  });

  const watched = watchlist.isWatched(listingId);

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Text variant="headline-medium" as="h2">
          {listingQuery.data ? listingQuery.data.product : t('marketplace.detail.title')}
        </Text>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* WatchStar — client-only watchlist toggle (see useWatchlist). */}
          <IconButton
            aria-label={
              watched
                ? t('watchlist.remove', 'Remove from watchlist')
                : t('watchlist.add', 'Add to watchlist')
            }
            onClick={() => watchlist.toggle(listingId)}
            sx={{ color: watched ? 'primary.main' : 'text.secondary' }}
          >
            <Icon name="star" size={24} fill={watched} color="inherit" />
          </IconButton>
          <Link to="/marketplace" style={{ textDecoration: 'none' }}>
            <Button variant="text">{t('marketplace.detail.back')}</Button>
          </Link>
        </Box>
      </Box>

      {listingQuery.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : listingQuery.isError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => listingQuery.refetch()}>
              {t('marketplace.retry')}
            </Button>
          }
        >
          {listingQuery.error instanceof FseApiError && listingQuery.error.status === 404
            ? t('marketplace.detail.notFound')
            : t('marketplace.loadError', {
                error:
                  listingQuery.error instanceof FseApiError
                    ? listingQuery.error.message
                    : String(listingQuery.error),
              })}
        </Alert>
      ) : (
        <ListingDetailBody listing={listingQuery.data} t={t} />
      )}
    </Stack>
  );
}

interface ListingDetailBodyProps {
  listing: ListingDto;
  t: TFunction;
}

function ListingDetailBody({ listing, t }: ListingDetailBodyProps) {
  const askPerKg = microToZarp(listing.askPriceZarpPerKgMicro);
  const isActive = listing.status === 'active';

  // Offers are buyer-only and never on your own listing — the server 403s
  // otherwise, so the UI must match (parity with mobile's gate). Identity is
  // read synchronously off the JWT claims (sub + role); no async store on web.
  const claims = readAccessTokenClaims();
  const isSeller = claims?.sub != null && claims.sub === listing.sellerUserId;
  const isBuyer = claims?.role === 'buyer';
  const canOffer = isActive && isBuyer && !isSeller;

  return (
    <Stack spacing={3}>
      {/* Summary card */}
      <Card sx={{ overflow: 'hidden' }}>
        {/* Cover image (TD-135) when present, else the eco-glyph fallback.
            ListingPhoto owns the tile dimensions + fallback (light + dark safe). */}
        <ListingPhoto
          listingId={listing.id}
          hasPhoto={listing.hasPhoto}
          height={160}
          glyphSize={64}
          alt={listing.product}
        />
        <CardHeader
          title={
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <Text variant="title-large" as="span">
                {listing.product}
              </Text>
              <StatusPill variant={STATUS_PILL[listing.status]}>
                {t(`marketplace.status.${listing.status}`)}
              </StatusPill>
            </Stack>
          }
          subheader={
            <Text variant="label-medium" color="text.secondary">
              {t('marketplace.seller', { name: listing.sellerName })}
            </Text>
          }
        />
        <CardContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            <Box>
              <Text variant="label-medium" color="text.secondary">
                {t('marketplace.quantity')}
              </Text>
              <Box sx={{ mt: 0.25 }}>
                <Text variant="title-medium" as="span">
                  {t('marketplace.qtyKg', { qty: listing.qtyKg })}
                </Text>
              </Box>
            </Box>
            <Box>
              <Text variant="label-medium" color="text.secondary">
                {t('marketplace.askPrice')}
              </Text>
              <Box sx={{ mt: 0.25 }}>
                <Money amount={askPerKg} unit={t('marketplace.perKg')} size="lg" />
              </Box>
            </Box>
            {listing.grade ? (
              <Box>
                <Text variant="label-medium" color="text.secondary">
                  {t('marketplace.grade')}
                </Text>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    kind="assist"
                    color={'neutral' as ChipColor}
                    label={t('marketplace.gradeValue', { grade: listing.grade })}
                  />
                </Box>
              </Box>
            ) : null}
            {listing.sellerCoopName ? (
              <Box>
                <Text variant="label-medium" color="text.secondary">
                  {t('marketplace.cooperative')}
                </Text>
                <Box sx={{ mt: 0.25 }}>
                  <Text variant="body-medium" as="span">
                    {listing.sellerCoopName}
                  </Text>
                </Box>
              </Box>
            ) : null}
          </Box>

          {listing.note ? (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Text variant="body-medium" color="text.secondary">
                {listing.note}
              </Text>
            </Box>
          ) : null}
        </CardContent>
      </Card>

      {/* Offer form — only for a verified BUYER on an active listing that isn't
          their own (the server 403s the seller / a non-buyer regardless, so the
          UI must match). Otherwise a neutral, honest notice rather than the
          form or a misleading "closed" message:
            - paused/withdrawn → not accepting offers
            - owner (any role)  → this is your own listing
            - non-buyer         → sign in as a buyer to make offers */}
      {canOffer ? (
        <OfferForm listing={listing} askPerKg={askPerKg} t={t} />
      ) : (
        <Alert severity="info">
          {!isActive
            ? t('marketplace.detail.notAcceptingOffers')
            : isSeller
              ? t('marketplace.detail.ownerNotice')
              : t('marketplace.detail.buyerOnly')}
        </Alert>
      )}
    </Stack>
  );
}

interface OfferFormProps {
  listing: ListingDto;
  askPerKg: number;
  t: TFunction;
}

function OfferForm({ listing, askPerKg, t }: OfferFormProps) {
  const queryClient = useQueryClient();
  const [price, setPrice] = useState('');
  // Sticky KYC-gate state: once a 403 buyer_kyc_required comes back, keep the
  // verification prompt visible even after the mutation object resets.
  const [kycGated, setKycGated] = useState(false);

  const micro = zarPerKgToMicroString(price);
  const priceValid = micro != null;
  // Estimated total = price/kg × full lot (whole-lot bid). Display only.
  const estTotal = priceValid ? Number(price) * Number(listing.qtyKg) : 0;

  const offerMutation = useMutation({
    mutationFn: (offerPriceZarpPerKgMicro: string) =>
      createOffer(listing.id, { offerPriceZarpPerKgMicro }),
    onSuccess: () => {
      setPrice('');
      setKycGated(false);
      // The buyer's own offers list + this listing's offerCount both change.
      queryClient.invalidateQueries({ queryKey: ['my-offers'] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-listing', listing.id] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-listings'] });
    },
    onError: (err) => {
      if (isBuyerKycRequired(err)) setKycGated(true);
    },
  });

  function submit() {
    if (!micro) return;
    setKycGated(false);
    offerMutation.mutate(micro);
  }

  // KYC gate takes over the whole card — the offer can't proceed until verified.
  if (kycGated) {
    return (
      <Card>
        <CardHeader title={t('marketplace.offer.title')} />
        <CardContent>
          <Stack spacing={2}>
            <Alert severity="warning">{t('marketplace.offer.kycRequired')}</Alert>
            <Text variant="body-medium" color="text.secondary">
              {t('marketplace.offer.kycRequiredBody')}
            </Text>
            <Box>
              <Link to="/buyer-verification" style={{ textDecoration: 'none' }}>
                <Button variant="contained">{t('marketplace.offer.getVerified')}</Button>
              </Link>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader title={t('marketplace.offer.title')} />
      <CardContent>
        <Stack spacing={2}>
          <Text variant="body-medium" color="text.secondary">
            {t('marketplace.offer.body', { qty: listing.qtyKg })}
          </Text>

          <TextField
            label={t('marketplace.offer.priceLabel')}
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            error={price.length > 0 && !priceValid}
            helperText={
              price.length > 0 && !priceValid
                ? t('marketplace.offer.priceError')
                : t('marketplace.offer.askHint', { ask: askPerKg })
            }
            disabled={offerMutation.isPending}
            fullWidth
          />

          {priceValid ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Text variant="label-medium" color="text.secondary">
                {t('marketplace.offer.estTotal')}
              </Text>
              <Money amount={estTotal} size="md" />
            </Box>
          ) : null}

          {offerMutation.isSuccess ? (
            <Alert severity="success">{t('marketplace.offer.success')}</Alert>
          ) : null}

          {offerMutation.isError && !kycGated ? (
            <Alert severity="error">
              {t('marketplace.offer.error', {
                error:
                  offerMutation.error instanceof FseApiError
                    ? offerMutation.error.message
                    : String(offerMutation.error),
              })}
            </Alert>
          ) : null}

          <Box>
            <Button
              variant="contained"
              onClick={submit}
              disabled={!priceValid || offerMutation.isPending}
              startIcon={offerMutation.isPending ? <CircularProgress size={16} /> : undefined}
            >
              {offerMutation.isPending
                ? t('marketplace.offer.submitting')
                : t('marketplace.offer.submit')}
            </Button>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
